
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { LoadingPage } from "./LoadingPage";
import { useAuthModal } from "@/hooks/use-auth-modal";
import { AuthModal } from "@/components/AuthModal";
import { ProfileSelectionModal } from "@/components/ProfileSelectionModal";

interface RequireAuthProps {
  children: React.ReactNode;
  requireProfileSelection?: boolean;
}

export function RequireAuth({ children, requireProfileSelection = true }: RequireAuthProps) {
  const { session, isLoading, profileSelectionComplete } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasCheckedProfile, setHasCheckedProfile] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const { 
    isAuthModalOpen, 
    isProfileSelectionOpen,
    openAuthModal, 
    closeAuthModal,
    openProfileSelection,
    closeProfileSelection
  } = useAuthModal();

  const checkProfileSelection = useCallback(() => {
    if (isLoading || !session) return false;

    // Check if we're processing a payment - if so, don't redirect to profiles
    const isProcessingPayment = sessionStorage.getItem('processing-payment') === 'true';
    if (isProcessingPayment) {
      console.log("Payment processing detected, staying on current page");
      return true;
    }

    // Check if we have payment parameters in URL - if so, don't redirect to profiles
    const searchParams = new URLSearchParams(location.search);
    const hasPaymentParams = searchParams.get('success') || searchParams.get('canceled') || searchParams.get('session_id');
    if (hasPaymentParams) {
      console.log("Payment parameters detected, staying on current page");
      return true;
    }

    // Profile selection is now handled globally in AuthProvider
    if (requireProfileSelection && !profileSelectionComplete) {
      return false;
    }
    
    return true;
  }, [session, isLoading, requireProfileSelection, profileSelectionComplete, location.search]);

  useEffect(() => {
    if (isLoading) return;

    if (!session) {
      // Navigate to home page and open auth modal
      navigate("/", { replace: true });
      openAuthModal("Please sign in to continue reading");
      return;
    }

    const canProceed = checkProfileSelection();
    if (canProceed) {
      setHasCheckedProfile(true);
      setIsInitialized(true);
    }
  }, [session, isLoading, checkProfileSelection, navigate, openAuthModal]);

  const handleAuthSuccess = () => {
    closeAuthModal();
    // The useAuth hook will automatically update the session state
    // and trigger a re-render, which will allow access to the protected route
  };

  const handleProfileSelected = () => {
    closeProfileSelection();
    setHasCheckedProfile(true);
    setIsInitialized(true);
  };

  if (isLoading || !isInitialized) {
    return <LoadingPage />;
  }

  if (!session) {
    return (
      <>
        <LoadingPage />
        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={closeAuthModal}
          onAuthSuccess={handleAuthSuccess}
          onTransitionToProfileSelection={() => {
            closeAuthModal();
            openProfileSelection();
          }}
          context="Please sign in to continue reading"
        />
      </>
    );
  }

  if (requireProfileSelection && !profileSelectionComplete) {
    return <LoadingPage />;
  }

  return <>{children}</>;
}
